import React from 'react';
import SeoMeta from '../components/SeoMeta';
import ArticleLayout from '../components/articles/ArticleLayout';
import { Callout, Checks, ArticleLink } from '../components/articles/blocks';

const FormatoPDFA1APage = () => {
  return (
    <>
      <SeoMeta
        title="Requisitos técnicos del DeCA 2026: PDF nativo, firma avanzada y eFTI | BlueCMR"
        description="Requisitos técnicos del DeCA según la Resolución de 5 de junio de 2026: PDF nativo digital, QR con URL HTTPS, firma avanzada y preparación eFTI."
        canonical="https://bluecmr.com/articulos/formato-pdfa1a-documento-control-digital-2026"
        type="article"
        keywords="DeCA requisitos técnicos, documento electrónico control administrativo, resolución junio 2026, BOE-A-2026-12784, PDF nativo digital, firma avanzada, eFTI, eIDAS, BlueCMR"
        breadcrumbs={[
          { name: 'Inicio', url: 'https://bluecmr.com/' },
          { name: 'Artículos', url: 'https://bluecmr.com/articulos' },
          { name: 'Requisitos técnicos DeCA 2026', url: 'https://bluecmr.com/articulos/formato-pdfa1a-documento-control-digital-2026' }
        ]}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: 'Requisitos técnicos del DeCA 2026: PDF nativo digital, firma avanzada y preparación eFTI',
            description: 'Desde el 5 de octubre de 2026, el Documento de Control es necesariamente digital. La Resolución de junio 2026 actualiza los requisitos técnicos: ya no se exige PDF/A, basta con PDF nativo digital.',
            image: 'https://www.bluecmr.com/og/articulo-requisitos.jpg',
            datePublished: '2025-01-15',
            dateModified: '2026-11-04',
            author: {
              '@type': 'Organization',
              name: 'BlueCMR',
              url: 'https://bluecmr.com'
            },
            publisher: {
              '@type': 'Organization',
              name: 'BlueCMR',
              logo: {
                '@type': 'ImageObject',
                url: 'https://www.bluecmr.com/og/logo-bluecmr.png'
              }
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://bluecmr.com/articulos/formato-pdfa1a-documento-control-digital-2026'
            },
            articleSection: 'Normativa',
            keywords: 'DeCA requisitos técnicos, resolución junio 2026, PDF nativo digital, firma avanzada, eFTI, eIDAS, BOE-A-2026-12784'
          }
        ]}
      />
      <ArticleLayout
        url="/articulos/formato-pdfa1a-documento-control-digital-2026"
        title="Requisitos técnicos del DeCA 2026: PDF nativo digital, firma avanzada y eFTI"
        deck="La Resolución de 5 de junio de 2026 actualiza los requisitos del DeCA (documento de control). Ya no se exige PDF/A — basta con PDF nativo digital. Le explicamos qué cambia y qué debe cumplir su solución."
        updated="2026-11-04"
        readingMinutes={5}
        toc={[
          { id: 'obligacion-legal', label: 'La obligación legal' },
          { id: 'resolucion-2026', label: 'La Resolución de 5 de junio de 2026' },
          { id: 'cambio-formato', label: 'De PDF/A a PDF estándar' },
          { id: 'firma', label: 'Firma electrónica' },
          { id: 'agrupacion', label: 'Agrupación de envíos' },
          { id: 'fuentes', label: 'Fuentes oficiales' },
        ]}
        closing={{ title: '¿Cumple su solución los requisitos técnicos del DeCA?', text: 'Le enseñamos cómo BlueCMR genera el DeCA conforme a la Resolución de 5 de junio de 2026 y con los datos preparados para eFTI, sin compromiso.' }}
      >
        <Callout title="Actualización publicada — 12 de junio de 2026">
          <p>
            La <strong>Resolución de 5 de junio de 2026</strong>, de la Dirección General de Transporte por Carretera y Ferrocarril (BOE-A-2026-12784), ha sustituido a la de 22 de mayo de 2023 y actualiza los requisitos técnicos del DeCA. <strong>Principales cambios:</strong> ya no se exige formato PDF/A — basta con PDF nativo digital (≤5 MB); se aclara que la firma no es obligatoria salvo uso contractual; y el dominio del repositorio no requiere comunicación previa. Esta página refleja los requisitos actualizados.
          </p>
        </Callout>

        <h2 id="obligacion-legal">1. La obligación legal: el DeCA es necesariamente digital</h2>
        <p>
          La <strong>Ley 9/2025, de Movilidad Sostenible</strong>, establece en su disposición transitoria octava que el Documento de Control Administrativo regulado en la Orden FOM/2861/2012 deberá emitirse <strong>necesariamente en formato digital</strong> una vez finalizado el periodo transitorio.
        </p>
        <p>
          <a href="https://www.boe.es/buscar/act.php?id=BOE-A-2025-24545" target="_blank" rel="noopener noreferrer">BOE – Ley 9/2025</a>
        </p>
        <p>
          Esto implica que el documento en papel ya no es válido y que las empresas deben contar con sistemas digitales conformes con los requisitos que determine la Administración.
        </p>
        <p>
          Aunque existe una amplia oferta de soluciones digitales en el mercado, <strong>no todas cumplen los requisitos técnicos y jurídicos</strong> que ya exige la normativa española, ni están preparadas para el escenario europeo que introduce el Reglamento eFTI.
        </p>
        <ArticleLink
          to="/documentos/documento-control"
          title="¿Qué es el DeCA?"
          text="Conozca en detalle qué es, cuándo es obligatorio y cómo cumplir con la normativa: guía completa del DeCA."
        />

        <h2 id="resolucion-2026">2. La Resolución de 5 de junio de 2026: requisitos técnicos actualizados del DeCA</h2>
        <p>
          La <strong>Resolución de 5 de junio de 2026</strong>, de la Dirección General de Transporte por Carretera y Ferrocarril (BOE núm. 143, 12/06/2026) establece las características que deben reunir los sistemas y los documentos electrónicos de control administrativo (DeCA). Sustituye a la Resolución de 22 de mayo de 2023.
        </p>
        <p>
          <a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2026-12784" target="_blank" rel="noopener noreferrer">BOE – Resolución de 5 de junio de 2026 (BOE-A-2026-12784)</a>
        </p>
        <p>La nueva resolución fija los siguientes requisitos:</p>
        <Checks
          items={[
            <>El documento debe ser un <strong>fichero PDF nativo digital</strong> (no escaneo ni imagen) con tamaño ≤5 MB</>,
            <>Debe incluir un <strong>código QR</strong> con la URL HTTPS del documento, que permita descarga directa sin autenticación</>,
            <>La aplicación debe registrar <strong>fecha y hora de creación y de modificación</strong> como metadatos del PDF</>,
            <>Los ficheros se almacenarán en un <strong>repositorio con URL única por documento</strong> (el dominio no requiere comunicación previa)</>,
            <>La firma electrónica <strong>no es obligatoria</strong>. Solo cuando el DeCA se use con finalidad contractual: mínimo firma avanzada (AdES) conforme a eIDAS</>,
          ]}
        />
        <Callout title="Cambio clave respecto a 2023">
          <p>Ya no se exige formato PDF/A. Basta con un PDF estándar nativo digital. El requisito principal pasa a ser la generación nativa (no escaneo), los metadatos de fechas y la accesibilidad vía QR/URL HTTPS.</p>
        </Callout>

        <h2 id="cambio-formato">3. Cambio de formato: de PDF/A a PDF estándar</h2>
        <p>
          La Resolución de 2023 (derogada) exigía formato PDF/A. La nueva Resolución de junio 2026 simplifica este requisito: basta con un <strong>fichero PDF estándar nativo digital</strong> (≤5 MB), con metadatos de fecha/hora de creación y modificación. No se aceptan ficheros obtenidos a partir de escaneo o imágenes digitalizadas.
        </p>
        <Callout>
          <p>El requisito clave ahora es la generación nativa digital, no el subestándar PDF/A. Lo relevante es que el PDF se genere a partir de datos estructurados, no de una imagen.</p>
        </Callout>

        <h2 id="firma">4. Firma electrónica: no obligatoria, pero esencial con finalidad contractual</h2>
        <p>
          La Resolución de 5 de junio de 2026 aclara expresamente que <strong>la firma no es un requisito de validez del DeCA</strong>, ya que ni la Orden FOM/2861/2012 ni la Orden FOM/1230/2013 establecen requisitos de firma para el documento de control.
        </p>
        <p>
          Sin embargo, cuando el DeCA se utilice también con <strong>finalidad contractual</strong> (por ejemplo, como Carta de Porte), la Resolución exige como mínimo una <strong>firma electrónica avanzada (AdES)</strong> conforme al Reglamento eIDAS. También son válidas las firmas cualificadas (QES).
        </p>
        <p>
          Los requisitos de la firma avanzada conforme a <strong>eIDAS (UE 910/2014)</strong>:
        </p>
        <Checks
          items={[
            'Identificación inequívoca del firmante',
            'Vinculación de la firma al documento',
            'Control exclusivo del firmante',
            'Detección de cualquier modificación posterior',
          ]}
        />
        <p>
          <a href="https://eur-lex.europa.eu/eli/reg/2014/910/oj" target="_blank" rel="noopener noreferrer">Reglamento eIDAS (UE 910/2014)</a>
        </p>
        <p>Esto excluye:</p>
        <ul>
          <li>Firmas simples</li>
          <li>"Clics de aceptación"</li>
          <li>Evidencias débiles que no cumplen los requisitos técnicos de firma avanzada</li>
        </ul>

        <h2 id="agrupacion">5. Agrupación de varios servicios en un mismo DeCA</h2>
        <p>
          La Resolución de junio 2026 (apartado Sexto) introduce la posibilidad de <strong>consignar varios envíos en un mismo documento electrónico de control administrativo</strong>, siempre que se cumplan determinadas condiciones:
        </p>
        <h3>Transporte de mercancías</h3>
        <Checks
          items={[
            <>El <strong>cargador contractual</strong> y el <strong>transportista efectivo</strong> deben ser los mismos en todos los envíos agrupados</>,
            <>Cada envío debe identificar claramente: <strong>origen y destino</strong>, y <strong>naturaleza y peso de la mercancía</strong></>,
            'Si otros datos asociados difieren entre envíos, también deben indicarse por separado',
            <>La secuencia de envíos en el DeCA <strong>no implica un orden de ejecución</strong></>,
          ]}
        />
        <Callout title="Ejemplo práctico">
          <p>Un cargador que envía 5 palés a diferentes destinos con el mismo transportista puede documentarlos en un solo DeCA, diferenciando origen/destino y mercancía de cada envío.</p>
        </Callout>

        <ArticleLink
          to="/articulos/digitalizacion-documento-control-transporte-datos-efti"
          title="Del PDF a los datos eFTI"
          text="Qué es eFTI y qué viene después del PDF: el calendario en España hasta la modificación del ROTT."
        />

        <h2 id="fuentes">6. Fuentes oficiales para verificación</h2>
        <ul>
          <li><a href="https://www.boe.es/buscar/act.php?id=BOE-A-2025-24545" target="_blank" rel="noopener noreferrer">Ley 9/2025, de Movilidad Sostenible (BOE)</a></li>
          <li><a href="https://www.boe.es/eli/es/o/2012/12/13/fom2861" target="_blank" rel="noopener noreferrer">Orden FOM/2861/2012 (Documento de Control)</a></li>
          <li><a href="https://www.boe.es/buscar/doc.php?id=BOE-A-2026-12784" target="_blank" rel="noopener noreferrer">Resolución de 5 de junio de 2026 (BOE-A-2026-12784) — Requisitos técnicos del DeCA</a></li>
          <li><a href="https://www.boe.es/eli/es/res/2023/05/22/(4)" target="_blank" rel="noopener noreferrer">Resolución de 22 de mayo de 2023</a> (derogada — sustituida por la de 5/06/2026)</li>
          <li><a href="https://www.transportes.gob.es/recursos_mfom/paginabasica/recursos/manual_app_documentos_control_1122.pdf" target="_blank" rel="noopener noreferrer">Manual de Usuario – Aplicación Documento de Control (MITMA)</a></li>
          <li><a href="https://eur-lex.europa.eu/eli/reg/2014/910/oj" target="_blank" rel="noopener noreferrer">Reglamento eIDAS (UE 910/2014)</a></li>
          <li><a href="https://eur-lex.europa.eu/eli/reg/2020/1056/oj" target="_blank" rel="noopener noreferrer">Reglamento eFTI (UE 2020/1056)</a></li>
        </ul>

      </ArticleLayout>
    </>
  );
};

export default FormatoPDFA1APage;
